import { api } from './api';
import type { PlatformId } from '@/shared/components/PlatformIcon';

/** GET /platforms/whatsapp/signup-config. Null ids: Embedded Signup is not configured on the server. */
export interface WhatsAppSignupConfig {
  appId: string | null;
  configId: string | null;
  graphVersion: string;
  manualTokenEnabled: boolean;
}

export interface PlatformStatus {
  platform: string;
  /** A connection exists, in any state (an expired one too). Whether it works is `active` / `connectState`. */
  connected: boolean;
  /** The connection works now (connectState ACTIVE). Optional: an older API does not send it. */
  active?: boolean;
  username: string | null;
  connectState: string;
  expiresAt: string | null;
  /** When it was connected; null when it is not. */
  connectedAt: string | null;
  /**
   * Whether this account may connect the channel now. False is "coming
   * soon" (an admin's channel switch); a connected channel works anyway.
   * Optional: an older API does not send it, which means available.
   */
  available?: boolean;
  /**
   * Facebook / Instagram: connected, but the Page to post as has not been
   * chosen yet (the person manages several). Optional for older APIs.
   */
  needsPageSelection?: boolean;
  /**
   * Whether the channel's post statistics can be read for this connection:
   * false for Telegram, WhatsApp, Google Business and a LinkedIn personal
   * profile. Optional for older APIs.
   */
  statsAvailable?: boolean;
  /**
   * Connected and working, but a reconnect would grant a missing permission.
   * `missing_scope`: an X connection made before media upload was asked
   * for posts text but not images or video. Optional for older APIs.
   */
  needsReconnect?: boolean;
  reconnectReason?: 'missing_scope' | null;
  /**
   * With `missing_scope`: what the connection cannot do until it is
   * reconnected with every permission (`channels.permission.<key>`).
   * Optional for older APIs.
   */
  missingPermissions?: PermissionFeature[];
  /**
   * The ways this server can connect the channel, when there is a choice
   * (Instagram: Instagram Login or a Facebook Page; LinkedIn: personal
   * profile or Company Page). Empty or absent: one way only.
   */
  connectMethods?: ConnectMethod[];
  /** How the existing connection was made; null for the platform's default way. */
  connectMethod?: ConnectMethod | null;
  /** LinkedIn Company Page: connected, but the Page to post as has not been chosen yet. */
  needsOrganizationSelection?: boolean;
  /** YouTube, TikTok: the platform has not approved UniPostudio yet, so it keeps every post private. */
  reviewPending?: boolean;
  /**
   * The channel's inbox waits on its platform (LinkedIn until it grants
   * inbox access, IA-13): publishing only, and the inbox leaves it out.
   */
  inboxHidden?: boolean;
  /**
   * When the connection stops working unless it is reconnected (the end of
   * Meta's data access, of a refresh token, or of a token that cannot renew
   * itself); null when it renews by itself. Optional for older APIs.
   */
  reconnectBy?: string | null;
  /** Posts waiting to publish on this channel after `reconnectBy`: they fail unless it is reconnected. */
  postsAtRisk?: number;
  /** Paused because the account's plan connects fewer channels per brand (D-01): publishes nothing. */
  pausedByPlan?: boolean;
}

/** How a connection is made, when a channel offers more than one way (platform-constants ConnectMethod). */
export type ConnectMethod = 'facebook_login' | 'instagram_login' | 'personal' | 'organization';

/** What a permission lets UniPostudio do (platform-constants PermissionFeature). */
export type PermissionFeature =
  | 'publish'
  | 'pages'
  | 'profile'
  | 'comments'
  | 'messages'
  | 'insights'
  | 'manageContent'
  | 'liveUpdates'
  | 'media'
  | 'boards'
  | 'businessProfile'
  | 'organizations';

/** One LinkedIn Company Page a Company Page connection can post as. */
export interface LinkedInOrganization {
  urn: string;
  name: string;
}

/** The two channels that post as a Facebook Page. */
export type MetaPagePlatform = 'facebook' | 'instagram';

/** One Facebook Page a Facebook or Instagram connection can post as. */
export interface MetaPage {
  id: string;
  name: string;
  /** Instagram: the linked Instagram business account. */
  igUserId?: string;
  igUsername?: string;
}

/** OAuth-capable platforms that support the authorize → callback flow. */
export const OAUTH_PLATFORMS: PlatformId[] = [
  'instagram',
  'facebook',
  'twitter',
  'linkedin',
  'pinterest',
  'youtube',
  'tiktok',
  'google_business',
];

/** GET /platforms/zetpin/connect-config. */
export interface ZetpinConnectConfig {
  oauthEnabled: boolean;
  serviceTokenEnabled: boolean;
}

/** One Business Profile location a Google Business connection can post to. */
export interface GoogleBusinessLocation {
  /** accounts/{a}/locations/{l} */
  name: string;
  title: string;
  address?: string;
}

export const platformsService = {
  /** Fetch connection status of every channel for the current workspace. */
  async getStatuses(): Promise<PlatformStatus[]> {
    const res = await api.get<PlatformStatus[]>('/platforms');
    return res.data;
  },

  /**
   * Get the OAuth authorization URL for a given platform. With credentials:
   * the API answers with an HttpOnly cookie binding the connect to this
   * browser, and its callback refuses a browser without it.
   */
  async getOAuthUrl(platform: PlatformId, method?: ConnectMethod): Promise<{ url: string; state: string }> {
    const res = await api.get<{ url: string; state: string }>(`/platforms/oauth/${platform.toUpperCase()}/url`, {
      withCredentials: true,
      ...(method ? { params: { method } } : {}),
    });
    return res.data;
  },

  /** The Company Pages a LinkedIn Company Page connection can post as, and the chosen one. */
  async getLinkedInOrganizations(): Promise<{ current: string | null; organizations: LinkedInOrganization[] }> {
    const res = await api.get<{ current: string | null; organizations: LinkedInOrganization[] }>('/platforms/linkedin/organizations');
    return res.data;
  },

  /** Chooses the Company Page a LinkedIn Company Page connection posts as. */
  async setLinkedInOrganization(organizationUrn: string): Promise<LinkedInOrganization> {
    const res = await api.post<LinkedInOrganization>('/platforms/linkedin/organization', { organizationUrn });
    return res.data;
  },

  /** Disconnect a platform from the current workspace. */
  async disconnect(platform: PlatformId): Promise<void> {
    await api.delete(`/platforms/${platform.toUpperCase()}`);
  },

  /** What WhatsApp Embedded Signup needs in the browser, and whether a pasted token is allowed. */
  async getWhatsAppSignupConfig(): Promise<WhatsAppSignupConfig> {
    const res = await api.get<WhatsAppSignupConfig>('/platforms/whatsapp/signup-config');
    return res.data;
  },

  /** Finishes Embedded Signup: the FB.login code and the WABA and number the customer chose. */
  async connectWhatsAppEmbeddedSignup(input: {
    code: string;
    wabaId: string;
    phoneNumberId: string;
    metaUserId?: string;
  }): Promise<{ message: string; platform: string; displayName: string | null }> {
    const res = await api.post('/platforms/whatsapp/embedded-signup', input);
    return res.data;
  },

  /**
   * Connect WhatsApp with a pasted System User token. Only for accounts an
   * admin enabled (`channel.whatsapp.manualToken`); customers use Embedded Signup.
   */
  async connectWhatsApp(input: {
    accessToken: string;
    phoneNumberId: string;
    wabaId: string;
  }): Promise<{ message: string; platform: string; displayName: string | null }> {
    const res = await api.post('/platforms/whatsapp/connect', input);
    return res.data;
  },

  /** The locations the Google Business connection can post to, and the chosen one. */
  async getGoogleBusinessLocations(): Promise<{ current: string | null; locations: GoogleBusinessLocation[] }> {
    const res = await api.get<{ current: string | null; locations: GoogleBusinessLocation[] }>(
      '/platforms/google-business/locations',
    );
    return res.data;
  },

  /** Points Google Business posts and review sync at another location. */
  async setGoogleBusinessLocation(name: string): Promise<GoogleBusinessLocation> {
    const res = await api.post<GoogleBusinessLocation>('/platforms/google-business/location', { name });
    return res.data;
  },

  /** The Pages a Facebook or Instagram connection can post as, and the chosen one (null until chosen). */
  async getMetaPages(platform: MetaPagePlatform): Promise<{ current: string | null; pages: MetaPage[] }> {
    const res = await api.get<{ current: string | null; pages: MetaPage[] }>(
      `/platforms/meta/${platform.toUpperCase()}/pages`,
    );
    return res.data;
  },

  /** Chooses the Page a Facebook or Instagram connection posts as. */
  async setMetaPage(platform: MetaPagePlatform, pageId: string): Promise<MetaPage> {
    const res = await api.post<MetaPage>(`/platforms/meta/${platform.toUpperCase()}/page`, { pageId });
    return res.data;
  },

  /** Manually connect Telegram using a bot token from @BotFather. */
  async connectTelegram(input: {
    botToken: string;
    defaultChatId?: string;
  }): Promise<{ message: string; platform: string; botUsername: string | null }> {
    const res = await api.post('/platforms/telegram/connect', input);
    return res.data;
  },

  /** How Zetpin can be connected on this server: OAuth (when configured) and a service token. */
  async getZetpinConnectConfig(): Promise<ZetpinConnectConfig> {
    const res = await api.get<ZetpinConnectConfig>('/platforms/zetpin/connect-config');
    return res.data;
  },

  /**
   * Connect Zetpin with a service token from the Zetpin team and the
   * account's handle. The API checks both with Zetpin before storing.
   */
  async connectZetpin(input: {
    serviceToken: string;
    accountHandle: string;
  }): Promise<{ message: string; platform: string; handle: string | null }> {
    const res = await api.post('/platforms/zetpin/connect', input);
    return res.data;
  },

  /**
   * Replay webhook subscription for every active connection.
   * Useful when connections existed before the auto-subscribe code
   * shipped, or when a platform-side webhook drop needs recovery.
   */
  async resubscribeWebhooks(): Promise<{
    attempted: number;
    succeeded: string[];
    failed: Array<{ platform: string; error: string }>;
  }> {
    const res = await api.post<{
      attempted: number;
      succeeded: string[];
      failed: Array<{ platform: string; error: string }>;
    }>('/platforms/resubscribe-webhooks');
    return res.data ?? { attempted: 0, succeeded: [], failed: [] };
  },
};
